import React, { useEffect, useRef, useState } from 'react'
import { Channel, Socket } from 'phoenix'
import { getTokenOnScriptLoad } from '~/utils/token'
import { v4 as uuidv4 } from 'uuid'
import { Loading } from '~/components/common/Loading'

type Status = 'loading' | 'success' | 'fail'

type State = {
  status: Status
  socket: Socket
  userChannel?: Channel
  campaignChannel?: Channel
  isCampaignChannelLoaded?: boolean
  tabUuid: string
  activeTab: boolean
}

const host = process.env.PHOENIX_CUSTOM_WEBSOCKET_PATH
  ? process.env.PHOENIX_CUSTOM_WEBSOCKET_PATH + 'phoenix/socket_phone'
  : 'wss://' + process.env.AGENT_DOMAIN + '/phoenix/socket_phone'

const tabUuid = uuidv4()

const socket = new Socket(host, {
  params: { token: getTokenOnScriptLoad(), uuid: tabUuid },
  heartbeatIntervalMs: 5000,
  logger(kind, message, data) {
    const event = new Event('iw-ws')
    event['kind'] = kind
    event['message'] = message
    event['data'] = data

    document.dispatchEvent(event)
  },
})

export function pushToChannel<T>(
  channel: Channel,
  eventName: string,
  data: any
) {
  return new Promise<T>((resolve, reject) => {
    channel
      .push(eventName, data)
      .receive('ok', response => {
        resolve(response)
      })
      .receive('error', response => {
        console.error(response.message, data)
        reject(response.message)
      })
  })
}

export const WebsocketContext = React.createContext<State>({
  status: 'loading',
  socket,
  tabUuid: '',
  activeTab: false,
})

type Props = {
  children: React.ReactNode
}

const WebsokcetConn: React.FC<Props> = ({ children }) => {
  const [status, setStatus] = useState<Status>('loading')
  const [isUserChannelLoaded, setUserChannelLoaded] = useState(false)
  const [isCampaignChannelLoaded, setCampaignChannelLoaded] = useState(false)
  const [activeTab, setActiveTab] = useState(false)
  const userChannelRef = useRef<Channel>()
  const campaignChannelRef = useRef<Channel>()
  const statusRef = useRef(status)
  statusRef.current = status

  useEffect(() => {
    let timer: NodeJS.Timer | undefined

    // odczytywanie tych danych z loggera jest lepsze niż odpytywanie onError
    // bo onError się nie zawsze wykonuje
    document.addEventListener('iw-ws', e => {
      const kind = e['kind']
      const message = e['message']

      if (kind == 'receive' && statusRef.current == 'fail') {
        // dla pewności ustawienie sukcesu po tym jak był heartbeat timeout
        setStatus('success')
      }

      if (kind == 'transport' && typeof message == 'string') {
        if (message.startsWith('connected')) {
          setStatus('success')
        }

        if (message.startsWith('heartbeat timeout')) {
          setStatus('fail')
        }

        if (message == 'close') {
          setStatus('fail')

          if (!timer) {
            timer = setInterval(() => {
              if (!socket.isConnected()) {
                console.log('---- socket reset')
                socket.disconnect()
                socket.connect()
              } else {
                clearInterval(timer)
                timer = undefined
              }
            }, 5000)
          }
        }
      }
    })

    socket.connect()

    socket.onOpen(() => {})

    socket.channel('phone_main').join()

    const userChannel = socket.channel('phone_user:' + window.c.userId)

    userChannelRef.current = userChannel

    userChannel.join().receive('ok', ({ active_tab }) => {
      setActiveTab(active_tab)
      setUserChannelLoaded(true)
    })

    userChannel.on('logout', () => {
      window.location = window.location
    })

    userChannel.on('ping', () => {
      userChannel.push('pong', {})
    })

    const campaignChannel = socket.channel(
      'phone_campaign:' + window.c.campaignId
    )

    campaignChannelRef.current = campaignChannel

    campaignChannel.join().receive('ok', () => {
      setCampaignChannelLoaded(true)
    })

    campaignChannel.on('presence', data => {
      if (data.type == 'online') {
        window.c.users = window.c.users.map(user =>
          data.users.includes(user.id) ? { ...user, online: true } : user
        )
      }

      if (data.type == 'offline') {
        window.c.users = window.c.users.map(user =>
          user.id == data.user.id ? { ...user, online: false } : user
        )
      }
    })
  }, [])

  return (
    <WebsocketContext.Provider
      value={{
        status,
        socket,
        userChannel: userChannelRef.current,
        campaignChannel: campaignChannelRef.current,
        isCampaignChannelLoaded,
        tabUuid,
        activeTab,
      }}
    >
      {!isUserChannelLoaded ? <Loading /> : children}
    </WebsocketContext.Provider>
  )
}

export default WebsokcetConn
